/* eslint-disable max-len */
import { ReactElement, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Loader from '../../components/Loader/Loader';
import NavigationPage from '../../components/NavigationPage/NavigationPage';
import PowerBiReport from '../../components/PowerBiReport/PowerBiReport';
import { useAuthorizationContext } from '../../hooks/useAuthorizationContext';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  clearReportError,
  selectReportError,
  selectReportList,
  selectReportLoading,
} from '../../redux/slices/reportSlice';
import { fetchTokenAndReportList, fetchTokenByReportID } from '../../redux/thunks/reportThunks';
import {
  getReportToken,
} from '../../services/base.service';
import { appRoutePaths } from '../../services/route.service';
import { ReportRetrievalStatus } from '../../types/ReportRetrievalStatus';
import { getReportEmbedUrlFromId } from '../../utilities/helperUtilities';
import './ReportDisplay.css';

const ReportDisplay = (): ReactElement => {
  const { reportId } = useParams();
  const dispatch = useAppDispatch();

  const reports = useAppSelector(selectReportList);
  const isLoading = useAppSelector(selectReportLoading);
  const reportError = useAppSelector(selectReportError);

  const [isLoadingReport, setIsLoadingReport] = useState<boolean>(true);
  const [reportRetrievalStatus, setReportRetrievalStatus] =
    useState<ReportRetrievalStatus>(ReportRetrievalStatus.LOADING);

  const embedUrl = getReportEmbedUrlFromId(reports, reportId);
  const { reportTokenStart } = useAuthorizationContext();
  
  const getBackLocation = (): string => {
    const savedTab = sessionStorage.getItem('dataAnalyticsActiveTab');
    const baseUrl = appRoutePaths.ReportDashboard;
    return savedTab ? `${baseUrl}?tab=${savedTab}` : baseUrl;
  };

  const queryReportToken = async (): Promise<boolean> => {
    dispatch(clearReportError());
    dispatch(fetchTokenAndReportList());
    await dispatch(fetchTokenByReportID(reportId || '')).unwrap();
     if (getReportToken()) {
      reportTokenStart();
    }
    return true;
  };

  const handleReportLoaded = (): void => {
    // Only set to RENDERING from LOADING.
    // This avoids getting into a weird state if Rendered is called before Loaded.
    if (reportRetrievalStatus == ReportRetrievalStatus.LOADING) {
      setReportRetrievalStatus(ReportRetrievalStatus.RENDERING);
    }
  };

  const handleReportRendered = (): void => {
    setReportRetrievalStatus(ReportRetrievalStatus.INACTIVE);
    setIsLoadingReport(false);
  };

  const handleReportError = (): void => {
    setReportRetrievalStatus(ReportRetrievalStatus.INACTIVE);
    setIsLoadingReport(false);
  };

  const [tokenQueried, setTokenQueried] = useState(false);

  useEffect(() => {
    let isMounted = true;
    if (!tokenQueried) {
      queryReportToken().then(() => {
        if (isMounted) setTokenQueried(true);
      });
    }
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenQueried]);

  return (
    <NavigationPage
      heading="View Report"
      pageClass="reports--view"
      isLoading={isLoadingReport && isLoading}
      loadingDataId="reports-loader"
      loadingText="Getting your report"
      backBarLocation={getBackLocation()}
      APIError={reportError}
    >
      {tokenQueried && !reportError && (
        <>
          {!embedUrl && (
            <div
              className="report--view--none empty-content"
              data-testid="report-empty-message"
            >
              <p>No report found or there was an issue loading your report</p>
            </div>
          )}
          {embedUrl && (
            <>
              {reportRetrievalStatus != ReportRetrievalStatus.INACTIVE && (
                <div className="report-retrieval-loader-container">
                  <Loader
                    message={`Report is ${reportRetrievalStatus}`}
                    dataTestId="report-retrieval-loader"
                  />
                </div>
              )}
              <PowerBiReport
                embedUrl={embedUrl}
                onLoaded={handleReportLoaded}
                onRendered={handleReportRendered}
                onError={handleReportError}
              />
            </>
          )}
        </>
      )}
    </NavigationPage>
  );
};

export default ReportDisplay;
